// The boot splash. Integrator-owned.
//
// The markup and its CSS live inline in index.html so they paint on the browser's first frame,
// before three.js is even fetched; this module only moves the bar and takes the splash away. If the
// element is missing (a showcase page, a test harness) every call here is a no-op.
//
// `elapsed()` is the number the stats overlay prints as "time to title": from navigation start to
// the moment the title screen is actually up.

const nowMs = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

/**
 * @param {Document} [doc]
 * @returns {{progress: Function, note: Function, done: Function, elapsed: () => number,
 *            timeToTitleMs: () => number|null, active: () => boolean}}
 */
export function createSplash(doc = typeof document !== 'undefined' ? document : null) {
  const root = doc ? doc.getElementById('splash') : null;
  const bar = doc ? doc.getElementById('splash-bar') : null;
  const note = doc ? doc.getElementById('splash-note') : null;
  const started = nowMs();
  let shown = 0;
  let finished = null;
  let removeTimer = null;

  /**
   * @param {number} value 0..1. Never goes backwards: a later stage reporting less than an earlier
   *   one (two loaders finishing out of order) would read as the bar sliding back.
   * @param {string} [text]
   */
  function progress(value, text) {
    if (!Number.isFinite(value)) return;
    const v = value < 0 ? 0 : value > 1 ? 1 : value;
    if (v > shown) {
      shown = v;
      if (bar) bar.style.width = `${(v * 100).toFixed(1)}%`;
    }
    if (text) setNote(text);
  }

  function setNote(text) {
    if (note && typeof text === 'string') note.textContent = text;
  }

  /** Fade out and unmount. Safe to call twice. */
  function done() {
    if (finished !== null) return;
    finished = nowMs();
    if (!root) return;
    if (bar) bar.style.width = '100%';
    root.classList.add('jst-splash-out');
    removeTimer = setTimeout(() => {
      root.remove();
      removeTimer = null;
    }, 460);
  }

  return {
    progress,
    note: setNote,
    done,
    elapsed: () => nowMs() - started,
    timeToTitleMs: () => (finished === null ? null : finished - navigationStart(started)),
    active: () => finished === null,
    dispose() {
      if (removeTimer) clearTimeout(removeTimer);
      if (root) root.remove();
    },
  };
}

/**
 * `performance.now()` is measured from navigation start in every browser that matters, so the
 * splash's own start time is only used as a fallback when it is not.
 */
function navigationStart(fallbackStart) {
  return typeof performance !== 'undefined' && performance.now ? 0 : fallbackStart;
}
